import { Fade, Paper, Popper } from "@mui/material";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type FC,
  type MouseEventHandler,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { useTopCalendar } from "@ryde/redux";
import {
  endOfZonedCalendarDay,
  isValidDateTime,
  MIN_DATE_VALUE,
  startOfZonedCalendarDay,
  topCalendarDatesAtomFamily,
  useTimezone,
  type RydeUiUnit,
  type TopCalendarMode,
} from "@ryde/shared";

import { MuiButton } from "../../controls";
import {
  TopCalendarDatesPanelStyled,
  TopCalendarDateTimeText,
  TopCalendarDropdown,
  TopCalendarPopoverFooter,
  TopCalendarPopperContent,
  TopCalendarWrapperStyled,
} from "./parts";
import { TopCalendarArrows } from "./parts/top-calendar.arrows";
import { useAllowTimeEditInMainPicker } from "./parts/top-calendar.hook";
import { TopCalendarPanel } from "./parts/top-calendar.panel";

const POPOVER_Y_OFFSET = 10;

const validateTopHeaderCalendar = ({
  start,
  end,
  errorMessage,
}: {
  start: TDateISO;
  end: TDateISO;
  errorMessage: string;
}): string[] => {
  const result: string[] = [];

  if (start === undefined && end === undefined) {
    return result;
  }

  if (start >= end) {
    result.push(errorMessage);
  }

  return result;
};

interface ITopHeaderCalendar {
  entity: RydeUiUnit;
  /** First-visit default range when there's no query-string / stored value (e.g. MONTH for the dashboard). */
  defaultMode?: TopCalendarMode;
}

export const TopHeaderCalendar: FC<ITopHeaderCalendar> = ({
  entity,
  defaultMode,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const {
    changeCalendarDates,
    setupInitialDates,
    canSetupInitialDates,
    dropdownItems,
    calendarMode,
    changeCalendarMode,
    navigatePrev,
    navigateNext,
  } = useTopCalendar(entity, defaultMode);
  const activeDatesUnit = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const allowTimeEditInMainPicker = useAllowTimeEditInMainPicker();
  const anchorElement = useRef<HTMLDivElement>(null);
  const [, startTransition] = useTransition();

  useEffect(() => {
    if (!activeDatesUnit && canSetupInitialDates) {
      startTransition(() => {
        setupInitialDates();
      });
    }
  }, [activeDatesUnit, canSetupInitialDates, setupInitialDates]);

  const [tempDateFrom, setTempDateFrom] = useState<TDateISO>(
    activeDatesUnit?.date1 || MIN_DATE_VALUE,
  );
  const [tempDateTo, setTempDateTo] = useState<TDateISO>(
    activeDatesUnit?.date2 || MIN_DATE_VALUE,
  );

  useEffect(() => {
    if (!open && activeDatesUnit) {
      setTempDateFrom(activeDatesUnit.date1 || MIN_DATE_VALUE);
      setTempDateTo(activeDatesUnit.date2 || MIN_DATE_VALUE);
    }
  }, [activeDatesUnit, open]);

  const validationErrors = useMemo(
    () =>
      validateTopHeaderCalendar({
        start: tempDateFrom,
        end: tempDateTo,
        errorMessage: t("errors.untilDateMustBeLaterThanFrom"),
      }),
    [tempDateFrom, tempDateTo, t],
  );

  const isFocusInsidePicker = (): boolean => {
    const activeElement = document.activeElement;
    if (!activeElement) {
      return false;
    }

    const isInput = activeElement.tagName === "INPUT";
    const isTextarea = activeElement.tagName === "TEXTAREA";
    const hasMuiPickerClass =
      activeElement.closest(".MuiPickersInputBase-root") !== null ||
      activeElement.closest(".MuiInputBase-root") !== null ||
      activeElement.closest(".MuiPickersSectionList-root") !== null;

    return isInput || isTextarea || hasMuiPickerClass;
  };

  const checkAndCorrectDateTo = useEvent((dateFrom: TDateISO): void => {
    if (!isValidDateTime(dateFrom)) {
      return;
    }

    setTempDateTo(prevTo => {
      if (dateFrom && prevTo && isValidDateTime(prevTo) && dateFrom >= prevTo) {
        const adjustedDateTo = endOfZonedCalendarDay({
          dateISO: dateFrom,
          timezone,
        })
          .utc()
          .toISOString();
        return adjustedDateTo;
      }
      return prevTo;
    });
  });

  const checkAndCorrectDateFrom = useEvent((dateTo: TDateISO): void => {
    if (!isValidDateTime(dateTo)) {
      return;
    }

    setTempDateFrom(prevFrom => {
      if (
        prevFrom &&
        dateTo &&
        isValidDateTime(prevFrom) &&
        prevFrom >= dateTo
      ) {
        const adjustedDateFrom = startOfZonedCalendarDay({
          dateISO: dateTo,
          timezone,
        })
          .utc()
          .toISOString();
        return adjustedDateFrom;
      }
      return prevFrom;
    });
  });

  const setTempDateFromCorrected = (dateFrom: TDateISO): void => {
    setTempDateFrom(dateFrom);
    if (!allowTimeEditInMainPicker && !isFocusInsidePicker()) {
      checkAndCorrectDateTo(dateFrom);
    }
  };

  const setTempDateToCorrected = (dateTo: TDateISO): void => {
    setTempDateTo(dateTo);
    if (!allowTimeEditInMainPicker && !isFocusInsidePicker()) {
      checkAndCorrectDateFrom(dateTo);
    }
  };

  const openEditor: MouseEventHandler<HTMLElement> = useEvent(() => {
    setTempDateFrom(activeDatesUnit?.date1 || MIN_DATE_VALUE);
    setTempDateTo(activeDatesUnit?.date2 || MIN_DATE_VALUE);
    setOpen(true);
  });

  const synchroDateValues = useEvent((): void => {
    if (
      activeDatesUnit &&
      (activeDatesUnit?.date1 !== tempDateFrom ||
        activeDatesUnit?.date2 !== tempDateTo)
    ) {
      changeCalendarDates({ date1: tempDateFrom, date2: tempDateTo });
    }
  });

  const closeDateEditor = useEvent((save?: boolean) => {
    if (save) {
      if (validationErrors.length) {
        return;
      }

      synchroDateValues();
    }

    setOpen(false);
  });
  return (
    <>
      {open && (
        <Popper
          open={open}
          anchorEl={anchorElement.current}
          placement={"bottom-start"}
          popperOptions={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, POPOVER_Y_OFFSET],
                },
              },
            ],
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper elevation={8}>
                <TopCalendarPopperContent>
                  <TopCalendarPanel
                    dateFrom={tempDateFrom}
                    dateTo={tempDateTo}
                    setDateFrom={setTempDateFromCorrected}
                    setDateTo={setTempDateToCorrected}
                    errors={validationErrors}
                  />
                </TopCalendarPopperContent>
                <TopCalendarPopoverFooter>
                  <MuiButton
                    muiSize={"small"}
                    onClick={() => closeDateEditor(true)}
                  >
                    {t("general.OK")}
                  </MuiButton>
                  <MuiButton
                    muiStyle={"primary"}
                    muiVariant={"outlined"}
                    muiSize={"small"}
                    onClick={() => closeDateEditor()}
                  >
                    {t("general.CANCEL")}
                  </MuiButton>
                </TopCalendarPopoverFooter>
              </Paper>
            </Fade>
          )}
        </Popper>
      )}
      {activeDatesUnit && (
        <TopCalendarWrapperStyled>
          <TopCalendarDropdown
            dropdownItems={dropdownItems}
            calendarMode={calendarMode}
            onChangeCalendarMode={changeCalendarMode}
            onCalendarDropdownOpen={() => closeDateEditor()}
          />
          <TopCalendarDatesPanelStyled
            ref={anchorElement}
            onClick={e => openEditor(e)}
          >
            <SVG_MUI.Calendar3SVG />
            <TopCalendarDateTimeText entity={entity} />
          </TopCalendarDatesPanelStyled>
          <TopCalendarArrows
            navigatePrev={navigatePrev}
            navigateNext={navigateNext}
          />
        </TopCalendarWrapperStyled>
      )}
    </>
  );
};
